import { type Op } from 'quill-delta';

import {
  type CollabFilter,
  type CollabFilterJSON,
  type MutableCollabFilter
} from './view-filter.ts';
import {
  type CollabGroupingJSON,
  type CollabGrouping,
  type MutableCollabGrouping
} from './view-grouping.ts';
import {
  type CollabViewSortType,
  type MutableSortArray,
  type SortArray
} from './view-sort.ts';

export enum CollabLayoutType {
  TABLE = 'table',
  GALLERY = 'gallery',
  BOARD = 'board',
  CALENDAR = 'calendar',
  TIMELINE = 'timeline',
  CARD = 'card' // Deprecated: Card referred as Gallery
}

const collabLayoutTypeValues = new Set<string>(Object.values(CollabLayoutType));

/**
 * Whether `layoutType` is a built-in {@link CollabLayoutType} rather than an
 * extension layout id registered through the view layout modules.
 */
export function isBuiltInCollabLayoutType(
  layoutType: string
): layoutType is CollabLayoutType {
  return collabLayoutTypeValues.has(layoutType);
}

export enum CollabCardWidth {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large'
}

export enum CollabCoverFieldType {
  None = 'none',
  Default = 'default',
  ID = 'id'
}

export enum CollabCalculationTypes {
  SUM = 's',
  COUNT = 'c',
  COUNT_EMPTY = 'ce',
  COUNT_TOTAL = 'ct',
  COUNT_UNIQUE = 'cu',
  PERCENT_EMPTY = 'pe',
  PERCENT_NOT_EMPTY = 'pne',
  AVERAGE = 'a',
  MAX = 'ma',
  MIN = 'mi'
}

export type CollabCoverField =
  | {
      type: CollabCoverFieldType.None;
    }
  | {
      type: CollabCoverFieldType.Default;
    }
  | {
      type: CollabCoverFieldType.ID;
      id: string;
    };

export type DatabaseViewJSON = {
  name: string;
  sort: CollabViewSortType | undefined;

  layoutType: CollabLayoutType | string;
  cardWidth: CollabCardWidth;
  coverField: CollabCoverField;
  showFieldNames: boolean;
  groupFieldId?: string;
  dateFieldId: string | undefined;
  showNumberedEntries: boolean;
  properties?: Record<string, unknown>;

  fields: Record<string, boolean> | undefined;

  filter: CollabFilterJSON | undefined;

  calculations: Record<string, CollabCalculationTypes>;

  sorts: CollabViewSortType[];

  grouping: CollabGroupingJSON | undefined;
};

/**
 * Generic patch shape for opaque layout-module properties.
 *
 * Core Databases owns the durable property bag, while registered layout
 * modules own validation and typed helpers for the keys they write.
 */
/**
 * Describes a partial update to a view's opaque layout properties bag.
 * `set` upserts key/value pairs; `unset` deletes keys. Both are optional
 * and are applied as independent Y.Map operations for conflict-free merging.
 */
export type PropertiesPatch = {
  set?: Record<string, unknown>;
  unset?: readonly string[];
};

export interface View {
  get name(): string;
  get sort(): CollabViewSortType | undefined;

  /**
   * Widened to `string` to support extension layout types registered
   * through the view layout modules registry.
   */
  get layoutType(): CollabLayoutType | string;
  get cardWidth(): CollabCardWidth;
  get coverField(): CollabCoverField;
  get showFieldNames(): boolean;
  get groupFieldId(): string | undefined;
  get dateFieldId(): string | undefined;
  get showNumberedEntries(): boolean;
  /**
   * Opaque bag of properties specific to the current layout type.
   * Interpretation is delegated to the corresponding
   * {@link ViewLayoutDataModule.parseProperties}.
   */
  get properties(): Readonly<Record<string, unknown> | undefined>;

  get fields(): Readonly<Record<string, boolean> | undefined>;

  get filter(): CollabFilter | undefined;

  get sorts(): SortArray;

  get grouping(): CollabGrouping | undefined;

  get calculations(): Readonly<Record<string, CollabCalculationTypes>>;
  setCalculation(fieldKey: string, value: CollabCalculationTypes): void;
  removeCalculation(fieldKey: string): void;

  toJSON(): DatabaseViewJSON;
}

export interface MutableView extends View {
  applyNameDelta(delta: Op[]): void;

  /** Accepts built-in {@link CollabLayoutType} or an extension layout ID string. */
  set layoutType(data: CollabLayoutType | string);
  set cardWidth(data: CollabCardWidth);
  set coverField(data: CollabCoverField);
  set showFieldNames(data: boolean);
  set groupFieldId(data: string | undefined);
  set dateFieldId(data: string | undefined);
  set showNumberedEntries(data: boolean);
  /** Replaces the full properties bag. Pass `undefined` to clear. */
  set properties(data: Record<string, unknown> | undefined);
  /**
   * Patches individual keys in the properties without replacing
   * the full bag. Preferred over the setter for concurrent edits.
   */
  updateProperties(patch: PropertiesPatch): void;

  setFieldVisibility(
    fieldKey: string,
    fieldValue: boolean,
    dbFields: readonly string[],
    options?: { includeNewFieldInValidation?: boolean }
  ): void;
  resetFieldVisibility(): void;
  removeFieldVisibilityField(fieldKey: string, dbFields: readonly string[]): void;

  set sort(data: CollabViewSortType | undefined);

  setFilter(val: CollabFilterJSON): MutableCollabFilter;
  clearFilter(): void;

  initGrouping(): MutableCollabGrouping;
  setGrouping(value: CollabGroupingJSON): MutableCollabGrouping;
  clearGrouping(): void;

  get filter(): MutableCollabFilter | undefined;
  get grouping(): MutableCollabGrouping | undefined;
  get sorts(): MutableSortArray;
  initializeSorts(): void;
}
